import React, { useEffect, useState } from "react";
import { Mail, ChevronDown, Menu, X, ArrowRight, Linkedin, Facebook, Instagram, LogIn } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import PhotonLogo from "./PhotonLogo";
import { navItems, contact } from "../mock";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const [mobileSub, setMobileSub] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    setOpenDrop(null);
    if (href.startsWith("/")) {
      navigate(href);
      window.scrollTo({ top: 0 });
      return;
    }
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top bar */}
      <div
        className="hidden md:block text-white text-sm"
        style={{ background: "linear-gradient(90deg, #050C17 0%, #0D1B2A 50%, #0EA5E9 100%)" }}
      >
        <div className="pp-container flex items-center justify-between h-10">
          <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-[#10B981] transition-colors">
            <Mail size={15} />
            <span>{contact.email}</span>
          </a>
          <div className="flex items-center gap-4">
            {[Linkedin, Facebook, Instagram].map((Icon, i) => (
              <a key={i} href="#footer" className="hover:text-[#10B981] transition-colors">
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className={`transition-all duration-300 ${scrolled ? "bg-white shadow-[0_8px_30px_rgba(13,27,42,0.10)]" : "bg-white/95 backdrop-blur"}`}>
        <div className="pp-container flex items-center justify-between h-[74px]">
          <button onClick={() => scrollTo("#top")} aria-label="PhotonPE home">
            <PhotonLogo variant="dark" />
          </button>

          {/* Desktop menu */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && setOpenDrop(item.label)}
                onMouseLeave={() => setOpenDrop(null)}
              >
                <button
                  onClick={() => scrollTo(item.href)}
                  className="flex items-center gap-1 text-[15px] font-medium text-[#0f2540] hover:text-[#0EA5E9] transition-colors"
                >
                  {item.label}
                  {item.children && <ChevronDown size={15} />}
                </button>
                {item.children && openDrop === item.label && (
                  <div className="absolute top-full left-0 pt-3">
                    <div className="w-56 bg-white rounded-xl shadow-[0_20px_50px_rgba(13,27,42,0.18)] border border-slate-100 p-2">
                      {item.children.map((c) => (
                        <button
                          key={c.label}
                          onClick={() => scrollTo(c.href)}
                          className="block w-full text-left px-3 py-2 rounded-lg text-sm text-[#1E293B] hover:bg-[#F0FDFA] hover:text-[#0EA5E9] transition-colors"
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => scrollTo("/login")}
              data-testid="header-login-button"
              className="hidden sm:flex items-center gap-2 font-semibold text-sm px-4 py-2.5 rounded-full text-[#0D1B2A] border border-slate-200 hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-colors"
            >
              <LogIn size={15} /> Login
            </button>
            <button
              onClick={() => scrollTo("#cta")}
              className="hidden sm:flex items-center gap-2 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-transform hover:scale-[1.03] active:scale-95"
              style={{ background: "linear-gradient(90deg, #06B6D4, #10B981)" }}
            >
              Reach Us
              <span className="w-6 h-6 rounded-full bg-white/25 flex items-center justify-center">
                <ArrowRight size={13} />
              </span>
            </button>
            <button className="lg:hidden text-[#0D1B2A]" onClick={() => setMobileOpen((v) => !v)}>
              {mobileOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-100 bg-white px-5 py-4 space-y-1 max-h-[75vh] overflow-y-auto">
            {navItems.map((item) => (
              <div key={item.label} className="border-b border-slate-50">
                <button
                  onClick={() => (item.children ? setMobileSub((v) => (v === item.label ? null : item.label)) : scrollTo(item.href))}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="flex w-full items-center justify-between text-left py-2.5 text-[15px] font-medium text-[#0f2540]"
                >
                  {item.label}
                  {item.children && <ChevronDown size={16} className={`transition-transform ${mobileSub === item.label ? "rotate-180" : ""}`} />}
                </button>
                {item.children && mobileSub === item.label && (
                  <div className="pb-2 pl-3 grid grid-cols-1">
                    {item.children.map((c) => (
                      <button key={c.label} onClick={() => { setMobileOpen(false); scrollTo(c.href); }}
                        data-testid={`mobile-nav-child-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="text-left py-2 text-sm text-[#475569] hover:text-[#06B6D4]">{c.label}</button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button
              onClick={() => scrollTo("/login")}
              data-testid="header-login-button-mobile"
              className="mt-3 w-full flex items-center justify-center gap-2 font-semibold py-3 rounded-full text-[#0D1B2A] border border-slate-200"
            >
              <LogIn size={15} /> Login
            </button>
            <button
              onClick={() => scrollTo("#cta")}
              className="mt-3 w-full text-white font-semibold py-3 rounded-full"
              style={{ background: "linear-gradient(90deg, #06B6D4, #10B981)" }}
            >
              Reach Us
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
